/* eslint-disable @typescript-eslint/ban-types */
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';
import { AndOrOption } from '@src/components/AdminHome/ChatsManagement/DialogMassiveAssignment/LogicalSelectors/AndOr';
import { ContainOption } from '@src/components/AdminHome/ChatsManagement/DialogMassiveAssignment/LogicalSelectors/Contains';
// eslint-disable-next-line import/no-cycle
import { useDispatch } from 'react-redux';

export type SummarizeChatResponse = { content: string };

export type SummarizeChatRequest = { chatId: string };

export type ReassignmentRequest = {
  assignedTo: number;
  assignToList: number[];
  dateType: 'LATEST_CONTACT_MESSAGE' | 'CREATION_DATE' | (string & {});
  from: string;
  to: string;
  departmentId?: number | null;
  tagFilter?: {
    operator: string;
    secondOperator: AndOrOption;
    tags: string[];
  };
};

export type ReassignmentResponse = {
  status: string;
  message: string;
};

export type ReassignmentTotalRequest = {
  dateType: 'LATEST_CONTACT_MESSAGE' | 'CREATION_DATE' | (string & {});
  from: string;
  to: string;
  assignedTo: number;
  departmentId?: number | null;
  tagFilter?: {
    operator: ContainOption;
    secondOperator: AndOrOption;
    tags: string[];
  };
};

export type ReassignmentTotalResponse = {
  totalChats: number;
};

export type ChatsBotTotalRequest = {
  from?: string;
  to?: string;
  dateNameField: 'LATEST_CONTACT_MESSAGE' | 'CREATION_DATE';
};

export type ChatsBotTotalResponse = {
  matchingChats: number;
  totalChatsInBotChatting: number;
  closingInProgress: boolean;
};

export type CloseChatsBotRequest = {
  from: string;
  to: string;
  merchantId: number;
  dateFilterType: 'LATEST_CONTACT_MESSAGE' | 'CREATION_DATE' | (string & {});
  sendExitMessageOnClosing?: boolean;
};

export const chatServicesApi = createApi({
  reducerPath: 'chatServices',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['summarize-chat', 'reassignment', 'chats-bot-total'],
  endpoints: ({ query, mutation }) => ({
    summarizeChat: query<SummarizeChatResponse, SummarizeChatRequest>({
      providesTags: ['summarize-chat'],
      query: request =>
        apiResource(B2ChatClient.resources.servicesChats.actions.summarize, {
          params: request,
        }),
    }),
    reassignment: mutation<ReassignmentResponse, ReassignmentRequest>({
      invalidatesTags: ['reassignment'],
      query: request =>
        apiResource(B2ChatClient.resources.chats.actions.reassignment, {
          data: request,
        }),
    }),
    reassignmentTotal: query<
      ReassignmentTotalResponse,
      ReassignmentTotalRequest
    >({
      providesTags: ['reassignment'],
      query: request =>
        apiResource(B2ChatClient.resources.chats.actions.reassignmentTotal, {
          data: request,
        }),
    }),
    chatsBotTotal: query<ChatsBotTotalResponse, ChatsBotTotalRequest>({
      providesTags: ['chats-bot-total'],
      keepUnusedDataFor: 120,

      query: request =>
        apiResource(
          B2ChatClient.resources.servicesChats.actions.chatsBotTotal,
          {
            params: request,
            withJWT: true,
          }
        ),
    }),
    closeChatsBot: mutation<void, CloseChatsBotRequest>({
      invalidatesTags: ['chats-bot-total'],
      query: ({ merchantId, sendExitMessageOnClosing = false, ...request }) =>
        apiResource(
          B2ChatClient.resources.servicesChats.actions.closeChatsBot,
          {
            params: {
              merchantId,
            },
            data: {
              ...request,
              sendExitMessageOnClosing,
              taskType: 'FROM_BOT_CHATTING_STATE',
            },
            withJWT: true,
          }
        ),
    }),
  }),
});

export const {
  useLazySummarizeChatQuery,
  useLazyReassignmentTotalQuery,
  useReassignmentTotalQuery,
  useReassignmentMutation,
  useLazyChatsBotTotalQuery,
  useChatsBotTotalQuery,
  useCloseChatsBotMutation,
} = chatServicesApi;

// Prefer exporting actions to invalidate, and let callers dispatch them where needed
export const invalidateCloseChatsBot = () =>
  chatServicesApi.util.invalidateTags(['chats-bot-total']);

export const useInvalidateCloseChatsBot = () => {
  const dispatch = useDispatch();
  return dispatch(invalidateCloseChatsBot());
};
